import { Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { sfsGet } from '../../utils/service-calls/o2o-capabilities';
import { getStoreById } from '../../utils/service-calls/sls';
import bopisConst from '../o2o-capabilities/bopis/constants';
import { sfs as sfsConst } from '../o2o-capabilities/sfs/constants';
import { ButtonBlack, ButtonWhite } from '../reusable';

import BulkResults from './BulkResults';
import ConfirmSubmitModal from './ConfirmSubmitModal';
import EditableValuesTable from './EditableValuesTable';
import {
  chunkArray, DEFAULT_CHUNK_SIZE, findIndexOfService,
} from './helpers';

const deriveBopisFromStore = (store) => {
  const services = (store && store.storeServices) || [];
  const idx = findIndexOfService(services, bopisConst.storeServiceTypeId);
  const entry = services[idx];
  if (!entry) return { enabled: false, leadTimeISO: null };
  return {
    enabled: !!entry.enabled,
    leadTimeISO: (entry.fulfillmentGuarantees && entry.fulfillmentGuarantees.leadTime) || null,
  };
};

const deriveSfsEnabledFromStore = (store) => {
  const services = (store && store.storeServices) || [];
  const idx = findIndexOfService(services, sfsConst.storeServiceTypeId);
  const entry = services[idx];
  return !!(entry && entry.enabled);
};

const Step2ConfigureAndSubmit = ({
  fulfillmentType,
  selectedStores,
  currentValuesCache,
  formValuesByStore,
  dirtyStoreIds,
  allRowsValid,
  changeSummary,
  submitState,
  progress,
  failures,
  failuresDownloaded,
  inProgressChunk,
  onCacheStore,
  onPrefetchError,
  onUpdateRowValues,
  onResetRow,
  onApplyEnabledToAll,
  onApplyLeadTimeToAll,
  onApplyDayToAll,
  onApplyExceptionsToAll,
  onRemoveStore,
  onPrevious,
  onRequestSubmit,
  onConfirmSubmit,
  onCancelSubmit,
  onDownloadFailures,
  onStartOver,
  onRetryFailed,
}) => {
  const accessToken = useSelector((state) => state.authorizationReducer.auth.accessToken);
  const [preFetchProgress, setPreFetchProgress] = useState({ done: false, processedCount: 0, total: 0 });
  const isMountedRef = useRef(true);

  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    if (!accessToken) return;
    const storesNeedingFetch = selectedStores.filter((s) => !currentValuesCache[s.id]);
    if (storesNeedingFetch.length === 0) {
      setPreFetchProgress({ done: true, processedCount: 0, total: 0 });
      return;
    }
    setPreFetchProgress({ done: false, processedCount: 0, total: storesNeedingFetch.length });

    const fetchOne = async (store) => {
      try {
        if (fulfillmentType === 'BOPIS') {
          const res = await getStoreById(accessToken, store.id);
          if (!isMountedRef.current) return;
          const rawStore = res.body;
          onCacheStore(store.id, { bopis: deriveBopisFromStore(rawStore), rawStore });
        } else {
          const [storeRes, sfsRes] = await Promise.all([
            getStoreById(accessToken, store.id),
            sfsGet(accessToken, store.id).catch((err) => ({ body: { objects: [] }, error: err })),
          ]);
          if (!isMountedRef.current) return;
          if (sfsRes && sfsRes.error && !(sfsRes.body && sfsRes.body.objects)) {
            throw sfsRes.error;
          }
          const rawStore = storeRes.body;
          const sfsRecord = (sfsRes.body && sfsRes.body.objects && sfsRes.body.objects[0]) || null;
          onCacheStore(store.id, {
            rawStore,
            sfs: sfsRecord,
            sfsEnabled: deriveSfsEnabledFromStore(rawStore),
          });
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        onPrefetchError(store.id, err && err.message ? err.message : 'Failed to fetch current values');
      } finally {
        if (isMountedRef.current) {
          setPreFetchProgress((prev) => ({
            ...prev,
            processedCount: prev.processedCount + 1,
          }));
        }
      }
    };

    const run = async () => {
      const chunks = chunkArray(storesNeedingFetch, DEFAULT_CHUNK_SIZE);
      // eslint-disable-next-line no-restricted-syntax
      for (const chunk of chunks) {
        if (!isMountedRef.current) return;
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(chunk.map(fetchOne));
      }
      if (isMountedRef.current) {
        setPreFetchProgress((prev) => ({ ...prev, done: true }));
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, fulfillmentType]);

  const hasPreFetchError = selectedStores.some((s) => currentValuesCache[s.id] && currentValuesCache[s.id].fetchError);
  const allStoresCached = selectedStores.every((s) => !!currentValuesCache[s.id]);
  const isSubmitting = submitState === 'running';
  const canSubmit = submitState === 'idle'
    && selectedStores.length > 0
    && allStoresCached
    && !hasPreFetchError
    && allRowsValid
    && dirtyStoreIds.length > 0;

  const isFetching = !preFetchProgress.done;
  const dirtyCount = dirtyStoreIds.length;
  const submitLabel = isSubmitting
    ? 'Running…'
    : `Submit (${dirtyCount} of ${selectedStores.length} store${selectedStores.length === 1 ? '' : 's'} changed)`;

  return (
    <section className="o2o-bulk-step2-section">
      {isFetching && preFetchProgress.total > 0 && (
        <article className="ncss-col-sm-12 ta-sm-c mt4-sm">
          <Loading />
          <p>{`Loading current values... ${preFetchProgress.processedCount} of ${preFetchProgress.total}`}</p>
        </article>
      )}

      {(!isFetching || allStoresCached) && (
        <EditableValuesTable
          currentValuesCache={currentValuesCache}
          formValuesByStore={formValuesByStore}
          fulfillmentType={fulfillmentType}
          isDisabled={isSubmitting}
          selectedStores={selectedStores}
          onApplyDayToAll={onApplyDayToAll}
          onApplyEnabledToAll={onApplyEnabledToAll}
          onApplyExceptionsToAll={onApplyExceptionsToAll}
          onApplyLeadTimeToAll={onApplyLeadTimeToAll}
          onRemoveStore={onRemoveStore}
          onResetRow={onResetRow}
          onUpdateRowValues={onUpdateRowValues}
        />
      )}

      {!allRowsValid && selectedStores.length > 0 && !isFetching && (
        <aside className="o2o-bulk-row-error mt2-sm">
          One or more rows have invalid values. Fix them or remove those stores to enable Submit.
        </aside>
      )}

      {submitState !== 'done' && (
        <div className="o2o-bulk-step2-footer">
          <ButtonWhite
            className="border"
            isDisabled={isSubmitting}
            label="‹ Previous"
            onClick={onPrevious}
          />
          <div className="o2o-bulk-step2-actions">
            <ButtonBlack
              isDisabled={!canSubmit}
              label={submitLabel}
              onClick={onRequestSubmit}
            />
          </div>
        </div>
      )}

      <ConfirmSubmitModal
        changeSummary={changeSummary}
        fulfillmentType={fulfillmentType}
        isOpen={submitState === 'confirming'}
        storeCount={dirtyCount}
        totalSelectedCount={selectedStores.length}
        onCancel={onCancelSubmit}
        onConfirm={onConfirmSubmit}
      />

      <BulkResults
        failures={failures}
        failuresDownloaded={failuresDownloaded}
        inProgressChunk={inProgressChunk}
        progress={progress}
        submitState={submitState}
        onDownloadFailures={onDownloadFailures}
        onRetryFailed={onRetryFailed}
        onStartOver={onStartOver}
      />
    </section>
  );
};

Step2ConfigureAndSubmit.defaultProps = {
  inProgressChunk: [],
};

Step2ConfigureAndSubmit.propTypes = {
  allRowsValid: PropTypes.bool.isRequired,
  changeSummary: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  currentValuesCache: PropTypes.shape().isRequired,
  dirtyStoreIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  failures: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  failuresDownloaded: PropTypes.bool.isRequired,
  formValuesByStore: PropTypes.shape().isRequired,
  fulfillmentType: PropTypes.oneOf(['BOPIS', 'SHIP_FROM_STORE']).isRequired,
  inProgressChunk: PropTypes.arrayOf(PropTypes.string),
  onApplyDayToAll: PropTypes.func.isRequired,
  onApplyEnabledToAll: PropTypes.func.isRequired,
  onApplyExceptionsToAll: PropTypes.func.isRequired,
  onApplyLeadTimeToAll: PropTypes.func.isRequired,
  onCacheStore: PropTypes.func.isRequired,
  onCancelSubmit: PropTypes.func.isRequired,
  onConfirmSubmit: PropTypes.func.isRequired,
  onDownloadFailures: PropTypes.func.isRequired,
  onPrefetchError: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onRemoveStore: PropTypes.func.isRequired,
  onRequestSubmit: PropTypes.func.isRequired,
  onResetRow: PropTypes.func.isRequired,
  onRetryFailed: PropTypes.func.isRequired,
  onStartOver: PropTypes.func.isRequired,
  onUpdateRowValues: PropTypes.func.isRequired,
  progress: PropTypes.shape().isRequired,
  selectedStores: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
  submitState: PropTypes.oneOf(['idle', 'confirming', 'running', 'done']).isRequired,
};

export default Step2ConfigureAndSubmit;
